/**
 * InventorySystem - Manages player inventory, item storage, and usage
 * Handles collectible items, key items, and consumables
 */

import { store } from '../../state/store';
import { heal, restoreStamina, setFlashlightBattery } from '../../state/slices/playerSlice';

export enum ItemType {
  // Consumables
  HEALTH_KIT = 'health_kit',
  BATTERY_PACK = 'battery_pack',
  STAMINA_BOOST = 'stamina_boost',

  // Key Items (quest/progression items)
  KEY_CARD_RED = 'key_card_red',
  KEY_CARD_BLUE = 'key_card_blue',
  KEY_CARD_YELLOW = 'key_card_yellow',
  SECURITY_CODE = 'security_code',
  EMPLOYEE_KEY = 'employee_key',
  MANAGER_KEY = 'manager_key',
  RESTRICTED_KEY = 'restricted_key',

  // Collectibles (lore items)
  NEWSPAPER = 'newspaper',
  EMPLOYEE_ID = 'employee_id',
  AUDIO_LOG = 'audio_log',
  PHOTO = 'photo',
  NOTE = 'note',

  // Weapons
  PISTOL = 'pistol',
  SHOTGUN = 'shotgun',

  // Ammo
  AMMO_PISTOL = 'ammo_pistol',
  AMMO_SHOTGUN = 'ammo_shotgun',
}

export interface ItemDefinition {
  id: string;
  type: ItemType;
  name: string;
  description: string;
  maxStack: number; // Maximum number that can be stacked (1 for unique items)
  consumable: boolean; // Can be used/consumed
  category: 'consumable' | 'key_item' | 'collectible';
}

export interface InventoryItem {
  itemId: string;
  quantity: number;
  acquiredAt: number; // Timestamp when first acquired
}

export class InventorySystem {
  private static instance: InventorySystem;

  // Item definitions database
  private itemDefinitions: Map<string, ItemDefinition> = new Map();

  // Player's current inventory
  private inventory: Map<string, InventoryItem> = new Map();

  // Inventory limits
  private maxSlots: number = 20; // Maximum inventory slots

  // Callbacks
  private onItemAddedCallbacks: Array<(item: ItemDefinition, quantity: number) => void> = [];
  private onItemRemovedCallbacks: Array<(item: ItemDefinition, quantity: number) => void> = [];
  private onItemUsedCallbacks: Map<string, (item: ItemDefinition) => boolean> = new Map();

  private constructor() {
    this.initializeItems();
  }

  public static getInstance(): InventorySystem {
    if (!InventorySystem.instance) {
      InventorySystem.instance = new InventorySystem();
    }
    return InventorySystem.instance;
  }

  /**
   * Initialize all item definitions
   */
  private initializeItems(): void {
    // Consumables
    this.addItemDefinition({
      id: 'health_kit',
      type: ItemType.HEALTH_KIT,
      name: 'Health Kit',
      description: 'Restores 50 HP',
      maxStack: 5,
      consumable: true,
      category: 'consumable',
    });

    this.addItemDefinition({
      id: 'battery_pack',
      type: ItemType.BATTERY_PACK,
      name: 'Battery Pack',
      description: 'Restores 30% flashlight battery',
      maxStack: 5,
      consumable: true,
      category: 'consumable',
    });

    this.addItemDefinition({
      id: 'stamina_boost',
      type: ItemType.STAMINA_BOOST,
      name: 'Energy Drink',
      description: 'Restores 40 stamina',
      maxStack: 5,
      consumable: true,
      category: 'consumable',
    });

    // Key Items
    this.addItemDefinition({
      id: 'key_card_red',
      type: ItemType.KEY_CARD_RED,
      name: 'Red Keycard',
      description: 'Opens red security doors',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'key_card_blue',
      type: ItemType.KEY_CARD_BLUE,
      name: 'Blue Keycard',
      description: 'Opens blue security doors',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'key_card_yellow',
      type: ItemType.KEY_CARD_YELLOW,
      name: 'Yellow Keycard',
      description: 'Opens yellow security doors',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'security_code',
      type: ItemType.SECURITY_CODE,
      name: 'Security Code',
      description: 'Written code: 1987',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'employee_key',
      type: ItemType.EMPLOYEE_KEY,
      name: 'Employee Key',
      description: 'Master key for employee-only areas on Floor 2',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'manager_key',
      type: ItemType.MANAGER_KEY,
      name: "Manager's Key",
      description: "Opens the Manager's Office on Floor 2",
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'restricted_key',
      type: ItemType.RESTRICTED_KEY,
      name: 'Restricted Area Key',
      description: 'Opens restricted areas on Floor 3',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    // Collectibles
    this.addItemDefinition({
      id: 'newspaper_1',
      type: ItemType.NEWSPAPER,
      name: 'Local Newspaper',
      description: '"Mysterious disappearances at Khan\'s Pizza..."',
      maxStack: 1,
      consumable: false,
      category: 'collectible',
    });

    this.addItemDefinition({
      id: 'employee_id_1',
      type: ItemType.EMPLOYEE_ID,
      name: 'Employee Badge',
      description: 'Belongs to Mark Thompson, Night Guard',
      maxStack: 1,
      consumable: false,
      category: 'collectible',
    });

    this.addItemDefinition({
      id: 'audio_log_1',
      type: ItemType.AUDIO_LOG,
      name: 'Audio Recording',
      description: 'Personal log from previous security guard',
      maxStack: 1,
      consumable: false,
      category: 'collectible',
    });

    // Weapons
    this.addItemDefinition({
      id: 'pistol',
      type: ItemType.PISTOL,
      name: 'Security Pistol',
      description: 'Standard-issue security handgun. Found in employee areas.',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    this.addItemDefinition({
      id: 'shotgun',
      type: ItemType.SHOTGUN,
      name: 'Pump Shotgun',
      description: 'Powerful at close range. Hidden deep in restricted areas.',
      maxStack: 1,
      consumable: false,
      category: 'key_item',
    });

    // Ammo
    this.addItemDefinition({
      id: 'ammo_pistol',
      type: ItemType.AMMO_PISTOL,
      name: 'Pistol Ammo',
      description: '9mm rounds. Works with the security pistol.',
      maxStack: 10,
      consumable: false,
      category: 'consumable',
    });

    this.addItemDefinition({
      id: 'ammo_shotgun',
      type: ItemType.AMMO_SHOTGUN,
      name: 'Shotgun Shells',
      description: '12-gauge shells for the pump shotgun.',
      maxStack: 6,
      consumable: false,
      category: 'consumable',
    });
  }

  /**
   * Add a new item definition to the database
   */
  public addItemDefinition(item: ItemDefinition): void {
    this.itemDefinitions.set(item.id, item);
  }

  /**
   * Get item definition by ID
   */
  public getItemDefinition(itemId: string): ItemDefinition | undefined {
    return this.itemDefinitions.get(itemId);
  }

  /**
   * Get all item definitions
   */
  public getAllItemDefinitions(): ItemDefinition[] {
    return Array.from(this.itemDefinitions.values());
  }

  /**
   * Get items by category
   */
  public getItemsByCategory(category: 'consumable' | 'key_item' | 'collectible'): ItemDefinition[] {
    return this.getAllItemDefinitions().filter(item => item.category === category);
  }

  /**
   * Add item to inventory
   */
  public addItem(itemId: string, quantity: number = 1): boolean {
    const itemDef = this.itemDefinitions.get(itemId);
    if (!itemDef) {
      console.error(`Item definition not found: ${itemId}`);
      return false;
    }

    // Check if inventory is full (for new items)
    if (!this.inventory.has(itemId) && this.inventory.size >= this.maxSlots) {
      console.warn('Inventory is full!');
      return false;
    }

    // Get or create inventory item
    const existingItem = this.inventory.get(itemId);

    if (existingItem) {
      // Check stack limit
      const newQuantity = existingItem.quantity + quantity;
      if (newQuantity > itemDef.maxStack) {
        console.warn(`Cannot add ${quantity} ${itemDef.name}. Stack limit is ${itemDef.maxStack}`);
        return false;
      }

      existingItem.quantity = newQuantity;
      this.inventory.set(itemId, existingItem);
    } else {
      // Add new item
      if (quantity > itemDef.maxStack) {
        quantity = itemDef.maxStack;
      }

      this.inventory.set(itemId, {
        itemId,
        quantity,
        acquiredAt: Date.now(),
      });
    }

    console.log(`📦 Added ${quantity}x ${itemDef.name} to inventory`);

    // Trigger callbacks
    this.onItemAddedCallbacks.forEach(callback => callback(itemDef, quantity));

    return true;
  }

  /**
   * Remove item from inventory
   */
  public removeItem(itemId: string, quantity: number = 1): boolean {
    const item = this.inventory.get(itemId);
    if (!item) {
      console.warn(`Item not in inventory: ${itemId}`);
      return false;
    }

    const itemDef = this.itemDefinitions.get(itemId);
    if (!itemDef) return false;

    if (item.quantity < quantity) {
      console.warn(`Not enough ${itemDef.name}. Have ${item.quantity}, need ${quantity}`);
      return false;
    }

    item.quantity -= quantity;

    if (item.quantity === 0) {
      this.inventory.delete(itemId);
      console.log(`📦 Removed ${itemDef.name} from inventory (depleted)`);
    } else {
      this.inventory.set(itemId, item);
      console.log(`📦 Used ${quantity}x ${itemDef.name}. ${item.quantity} remaining`);
    }

    // Trigger callbacks
    this.onItemRemovedCallbacks.forEach(callback => callback(itemDef, quantity));

    return true;
  }

  /**
   * Use/consume an item
   */
  public useItem(itemId: string): boolean {
    const itemDef = this.itemDefinitions.get(itemId);
    if (!itemDef) {
      console.error(`Item definition not found: ${itemId}`);
      return false;
    }

    if (!itemDef.consumable) {
      console.warn(`${itemDef.name} is not consumable`);
      return false;
    }

    if (!this.hasItem(itemId)) {
      console.warn(`Don't have ${itemDef.name} in inventory`);
      return false;
    }

    // Check if there's a custom use callback registered
    const useCallback = this.onItemUsedCallbacks.get(itemId);
    if (useCallback) {
      const success = useCallback(itemDef);
      if (success) {
        this.removeItem(itemId, 1);
      }
      return success;
    }

    // Default consumable behavior
    const state = store.getState();
    let success = false;

    switch (itemDef.type) {
      case ItemType.HEALTH_KIT:
        // Restore 50 HP
        store.dispatch(heal(50));
        console.log('💚 Used Health Kit - Restored 50 HP');
        success = true;
        break;

      case ItemType.BATTERY_PACK:
        // Restore 30% battery
        const currentBattery = state.player.flashlightBattery;
        const newBattery = Math.min(100, currentBattery + 30);
        store.dispatch(setFlashlightBattery(newBattery));
        console.log('🔋 Used Battery Pack - Restored 30% battery');
        success = true;
        break;

      case ItemType.STAMINA_BOOST:
        // Restore 40 stamina
        store.dispatch(restoreStamina(40));
        console.log('⚡ Used Energy Drink - Restored 40 stamina');
        success = true;
        break;

      default:
        console.warn(`No use behavior defined for ${itemDef.name}`);
    }

    if (success) {
      this.removeItem(itemId, 1);
    }

    return success;
  }

  /**
   * Check if player has an item
   */
  public hasItem(itemId: string, quantity: number = 1): boolean {
    const item = this.inventory.get(itemId);
    return item ? item.quantity >= quantity : false;
  }

  /**
   * Get item quantity in inventory
   */
  public getItemQuantity(itemId: string): number {
    const item = this.inventory.get(itemId);
    return item ? item.quantity : 0;
  }

  /**
   * Get all items in inventory
   */
  public getInventory(): InventoryItem[] {
    return Array.from(this.inventory.values());
  }

  /**
   * Get inventory items by category
   */
  public getInventoryByCategory(category: 'consumable' | 'key_item' | 'collectible'): InventoryItem[] {
    return this.getInventory().filter(invItem => {
      const itemDef = this.itemDefinitions.get(invItem.itemId);
      return itemDef?.category === category;
    });
  }

  /**
   * Get current inventory size (number of unique items)
   */
  public getInventorySize(): number {
    return this.inventory.size;
  }

  /**
   * Get maximum inventory slots
   */
  public getMaxSlots(): number {
    return this.maxSlots;
  }

  /**
   * Set maximum inventory slots
   */
  public setMaxSlots(slots: number): void {
    this.maxSlots = slots;
  }

  /**
   * Clear entire inventory (for new game, etc.)
   */
  public clearInventory(): void {
    this.inventory.clear();
    console.log('🗑️ Inventory cleared');
  }

  /**
   * Register callback for when item is added
   */
  public onItemAdded(callback: (item: ItemDefinition, quantity: number) => void): void {
    this.onItemAddedCallbacks.push(callback);
  }

  /**
   * Register callback for when item is removed
   */
  public onItemRemoved(callback: (item: ItemDefinition, quantity: number) => void): void {
    this.onItemRemovedCallbacks.push(callback);
  }

  /**
   * Register custom use behavior for an item
   */
  public registerItemUse(itemId: string, callback: (item: ItemDefinition) => boolean): void {
    this.onItemUsedCallbacks.set(itemId, callback);
  }

  /**
   * Get inventory statistics
   */
  public getStats(): {
    totalItems: number;
    consumables: number;
    keyItems: number;
    collectibles: number;
    slotsUsed: number;
    slotsAvailable: number;
  } {
    const inventory = this.getInventory();
    const consumables = this.getInventoryByCategory('consumable').length;
    const keyItems = this.getInventoryByCategory('key_item').length;
    const collectibles = this.getInventoryByCategory('collectible').length;

    return {
      totalItems: inventory.reduce((sum, item) => sum + item.quantity, 0),
      consumables,
      keyItems,
      collectibles,
      slotsUsed: this.inventory.size,
      slotsAvailable: this.maxSlots - this.inventory.size,
    };
  }

  /**
   * Export inventory data (for save system)
   */
  public exportInventory(): InventoryItem[] {
    return this.getInventory();
  }

  /**
   * Import inventory data (for load system)
   */
  public importInventory(items: InventoryItem[]): void {
    this.clearInventory();
    items.forEach(item => {
      this.inventory.set(item.itemId, item);
    });
    console.log(`📦 Imported ${items.length} items into inventory`);
  }
}

// Export singleton instance
export const inventorySystem = InventorySystem.getInstance();
